import React, {CSSProperties, ReactNode, useCallback, useMemo, useState} from 'react';
import {ClickAwayListener, Fade, IconButton, IconButtonProps, Paper, Popper} from '@mui/material';
import {nanoid} from 'nanoid';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {PopperPlacementType} from '@mui/material/Popper';
import clsx from 'clsx';
import styles from './ButtonWithPopper.module.scss';

type ThemeType = 'expandedButton';

type ButtonWithPopperProps = {
  popperButtonSize?: IconButtonProps['size'];
  group?: string;
  renderButton?: (p: any) => ReactNode;
  renderPopperContent: (p: any) => ReactNode;
  placement?: PopperPlacementType;
  style?: CSSProperties;
  theme?: ThemeType;
};
const ButtonWithPopper: React.FC<ButtonWithPopperProps> = ({
  popperButtonSize,
  group: _group,
  placement = 'bottom',
  renderButton,
  renderPopperContent,
  style,
  theme,
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const target = event.currentTarget;
      setAnchorEl((prev) => (prev === target ? null : target));
      setOpen((prev) => !prev);
    },
    [setAnchorEl, setOpen],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const buttonProps = useMemo(() => {
    return {
      'aria-controls': open ? `menu-${nanoid()}` : undefined,
      'aria-expanded': open,
      'aria-haspopup': true,
      onClick: handleClick,
    };
  }, [handleClick, open]);

  return (
    <div style={{display: 'inline-block', ...style}} className={theme}>
      <div className={clsx(styles.buttonWrapper, {[styles.buttonWrapperOpen]: open})}>
        {renderButton ? (
          renderButton(buttonProps)
        ) : (
          <IconButton {...buttonProps} size={popperButtonSize}>
            <MoreHorizIcon />
          </IconButton>
        )}
      </div>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        transition
        disablePortal={true}
        modifiers={[
          {
            name: 'flip',
            enabled: false,
          },
          {
            name: 'preventOverflow',
            enabled: false,
            options: {
              boundary: 'scrollParent',
            },
          },
        ]}
        style={{zIndex: 1300}}
      >
        {({TransitionProps}) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <div>{renderPopperContent({handleClose})}</div>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </div>
  );
};

export default ButtonWithPopper;
